import { api, edenFetch } from "@/lib/eden";

/**
 * Birthday Request responder data contracts (PRD 05 §6.3–6.5, §8.3): the token-resolved
 * form, the D8 submit, and the §6.5 save-requester funnel. GET/POST /api/dob/:token return
 * Kysely rows via `t.Any()`, so these hand-type the slice the responder surface reads
 * (same convention as sign-data.ts).
 */

/** A month/day/(optional)year triple — the wire shape the submit endpoint expects. */
export interface BrDate {
  month: number;
  day: number;
  year?: number | null;
}

/** Pre-fill for the emailed scope: the bound contact's current record. */
export interface BrPrefill {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  relationshipCode: string | null;
  /** A member answering: their own birthday (year null when hidden / unknown). */
  birthday?: { month: number; day: number; year: number | null } | null;
}

/** GET /api/dob/:token — expired dead-link, or the scope-appropriate form. */
export type BrPayload =
  | { expired: true; requester_name: string }
  | {
      expired: false;
      scope: "emailed" | "general";
      requester_name: string;
      prefill: BrPrefill | null;
      /** D10: the GET soft-logged this responder in server-side. Unread by the UI (the
       *  submit's `responder_is_member` covers the funnel branch); kept as contract. */
      soft_login: boolean;
    };

/** POST body — mirrors the server `submit` schema (firstName required, rest optional). */
export interface BrSubmitInput {
  firstName: string;
  lastName?: string | null;
  birthday: BrDate;
  anniversary?: BrDate | null;
  relationshipCode?: string | null;
  email?: string | null;
}

/**
 * POST /api/dob/:token/submit result — drives the confirmation funnel. `responder_is_member`
 * (true for a full OR D10 soft-login session) branches it: member → one-tap save; else register.
 */
export interface BrSubmitResult {
  saved: true;
  scope: "emailed" | "general";
  requester: { user_id: string; display_name: string | null };
  responder_is_member: boolean;
}

/** POST /api/dob/:token/save-requester result (§6.5 funnel). */
export interface SaveRequesterResult {
  ok: true;
  contactId: string;
  reminderCreated: boolean;
}

export const brKeys = {
  responder: (token: string) => ["/api/dob", token] as const,
};

/** Resolve the responder form by its token (public — the token IS the credential). */
export const fetchBr = (token: string) =>
  edenFetch(api.api.dob({ token }).get()) as Promise<BrPayload>;

/** Submit the birthday (the D8 write). Throws `ApiError` — 410 `br_expired` if it lapsed. */
export const submitBr = (token: string, input: BrSubmitInput) =>
  edenFetch(
    api.api.dob({ token }).submit.post({
      firstName: input.firstName,
      lastName: input.lastName ?? null,
      birthday: input.birthday,
      anniversary: input.anniversary ?? null,
      relationshipCode: input.relationshipCode ?? null,
      email: input.email ?? null,
    }),
  ) as Promise<BrSubmitResult>;

/**
 * Save the requester's birthday to the responder's account (§6.5 funnel). Requires a session
 * (full or the D10 soft-login the emailed GET started). Throws `ApiError` 401 if logged out.
 */
export const saveRequester = (token: string) =>
  edenFetch(api.api.dob({ token })["save-requester"].post()) as Promise<SaveRequesterResult>;

/** Server 410 detail when a submit bounces off the 30-day expiry. */
export const BR_EXPIRED = "br_expired";
